import { ThemeEvents } from "@theme/events";

/**
 * Keeps the PLP v2 header count row honest without a page reload:
 *
 * - The item total mirrors the product grid's own count display
 *   (`.products-count-wrapper`) after each Section Rendering API morph,
 *   so the row can never disagree with the grid the shopper is looking
 *   at — including whatever the grid shows on back/forward navigation.
 * - The applied-filter status is derived from the query params carried
 *   by the theme's bubbling `FilterUpdateEvent`.
 *
 * Filters-dev boundary: the filter/sort redesign (separate workstream)
 * plugs in through the same `FilterUpdateEvent` contract. This element
 * only consumes that event — it must never reach into the facets module.
 */
class CountRowComponent extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    document.addEventListener(
      ThemeEvents.filterUpdate,
      this.#handleFilterUpdate,
    );
    this.#observeGridCount();
  }

  disconnectedCallback() {
    document.removeEventListener(
      ThemeEvents.filterUpdate,
      this.#handleFilterUpdate,
    );
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Counts the filters applied in a set of query params. Every non-empty
   * `filter.*` param counts once, except range pairs (`.gte`/`.lte`, e.g.
   * price) which collapse to a single applied filter.
   * @param {URLSearchParams} queryParams
   * @returns {number}
   */
  static countAppliedFilters(queryParams) {
    let count = 0;
    const rangeFilters = new Set();

    for (const [key, value] of queryParams.entries()) {
      if (!key.startsWith("filter.") || value === "") continue;

      const rangeMatch = key.match(/^(.+)\.(gte|lte)$/);
      if (rangeMatch) {
        rangeFilters.add(rangeMatch[1]);
      } else {
        count += 1;
      }
    }

    return count + rangeFilters.size;
  }

  /** @param {import('@theme/events').FilterUpdateEvent} event */
  #handleFilterUpdate = (event) => {
    const filterStatus = this.querySelector("[data-filter-status]");
    const queryParams = event.detail?.queryParams;
    if (!filterStatus || !queryParams) return;

    const count = CountRowComponent.countAppliedFilters(queryParams);

    if (count === 0) {
      filterStatus.textContent = this.dataset.placeholderText ?? "";
    } else if (count === 1) {
      filterStatus.textContent = this.dataset.filtersAppliedOne ?? "";
    } else {
      // The "other" plural form is server-rendered with a 9999 sentinel
      // count so the number can be swapped in without re-implementing the
      // translation's word order client-side.
      filterStatus.textContent = (this.dataset.filtersAppliedOther ?? "")
        .replace("9999", String(count));
    }
  };

  #observeGridCount() {
    const gridSection = document
      .querySelector(".products-count-wrapper")
      ?.closest(".shopify-section");
    if (!gridSection) return;

    this.#observer = new MutationObserver(() => this.#mirrorGridCount());
    this.#observer.observe(gridSection, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  #mirrorGridCount() {
    const itemCount = this.querySelector("[data-item-count]");
    const gridCount = document.querySelector(".products-count-wrapper");
    if (!itemCount || !gridCount) return;

    const text = gridCount.textContent.trim();
    if (text && text !== itemCount.textContent) {
      itemCount.textContent = text;
    }
  }
}

if (!customElements.get("count-row-component")) {
  customElements.define("count-row-component", CountRowComponent);
}

export { CountRowComponent };
